/**
 * 🎯 Hook useUserData - Helper mutualisé pour la gestion invités/connectés
 *
 * Ce hook centralise la logique de détection et de récupération des données
 * pour les utilisateurs invités vs connectés, utilisable par le dashboard,
 * le journal, et d'autres composants.
 */

import { useMemo } from "react";
import { useAuth } from "@core/contexts/AuthContext";
import { getCurrentUserId } from "@core/lib/guestIdentityService";
import { logger } from "@core/lib/logger";

export interface UserData {
  /** L'utilisateur est-il connecté ? */
  isAuthenticated: boolean;
  /** L'utilisateur est-il un invité ? */
  isGuest: boolean;
  /** ID utilisateur Supabase (si connecté) */
  userId?: string;
  /** Device ID (pour les invités) */
  deviceId: string;
  /** ID à utiliser pour le filtrage/stockage */
  currentUserId: string;
}

export interface UserDataOptions {
  /** Activer les logs détaillés (défaut: false) */
  enableLogging?: boolean;
  /** Contexte pour les logs (ex: "dashboard", "journal") */
  logContext?: string;
}

/**
 * Hook pour gérer la logique invité vs connecté de manière unifiée
 *
 * @param options - Options de configuration
 * @returns Objet UserData avec toutes les informations nécessaires
 */
export function useUserData(options: UserDataOptions = {}): UserData {
  const { enableLogging = false, logContext = "useUserData" } = options;
  const { user } = useAuth();

  const userData = useMemo(() => {
    const isAuthenticated = !!user?.id;
    const isGuest = !isAuthenticated;
    const deviceId = getCurrentUserId(); // Device ID pour les invités
    const currentUserId = getCurrentUserId(user?.id); // user.id si connecté, sinon deviceId

    if (enableLogging) {
      logger.debug(`🔍 ${logContext} - User Data`, logContext as any, {
        isAuthenticated,
        isGuest,
        userId: user?.id,
        deviceId,
        currentUserId,
        userEmail: user?.email,
      });
    }

    return {
      isAuthenticated,
      isGuest,
      userId: user?.id,
      deviceId,
      currentUserId,
    };
  }, [user, enableLogging, logContext]);

  return userData;
}

/**
 * Helper pour filtrer des items selon le mode invité/connecté
 *
 * @param items - Items à filtrer (polls, conversations, etc.)
 * @param userData - Données utilisateur retournées par useUserData
 * @param options - Options de filtrage
 * @returns Items filtrés
 */
export function filterByUserMode<T extends Record<string, any>>(
  items: T[],
  userData: UserData,
  options: {
    /** Champ contenant l'ID du créateur (défaut: "creator_id") */
    creatorField?: string;
    /** Fonction pour déterminer si un item appartient à un invité */
    isGuestItem?: (item: T) => boolean;
    /** Activer les logs de filtrage */
    enableLogging?: boolean;
    /** Contexte pour les logs */
    logContext?: string;
  } = {},
): T[] {
  const {
    creatorField = "creator_id",
    isGuestItem = (item) => {
      // Logique par défaut pour détecter un item invité
      const creatorId = item[creatorField];
      const guestFingerprint = (item as any).guest_fingerprint;

      // Un item est un item invité s'il n'a pas de creator_id OU si c'est un guest explicite OU s'il a un guest_fingerprint
      const isGuestFormat =
        !creatorId ||
        creatorId === null ||
        creatorId === undefined ||
        guestFingerprint ||
        (typeof creatorId === "string" && creatorId.startsWith("guest-"));

      // En mode invité, on vérifie aussi que l'appartient à l'invité actuel
      if (isGuestFormat) {
        // Vérifier si le creator_id correspond au deviceId actuel
        const matchesDeviceId = creatorId === userData.currentUserId;
        // Vérifier si le guest_fingerprint correspond au deviceId actuel
        const matchesFingerprint = guestFingerprint === userData.deviceId;

        return matchesDeviceId || matchesFingerprint;
      }

      return false;
    },
    enableLogging = false,
    logContext = "filterByUserMode",
  } = options;

  const filtered = items.filter((item) => {
    if (userData.isAuthenticated) {
      // Mode connecté : seulement les items de l'utilisateur
      const matches = item[creatorField] === userData.userId;

      if (enableLogging && !matches) {
        logger.debug(`🔍 ${logContext} - Item exclu (mode connecté)`, logContext as any, {
          itemId: item.id,
          creatorId: item[creatorField],
          userId: userData.userId,
        });
      }

      return matches;
    } else {
      // Mode invité : seulement les items invités
      const matches = isGuestItem(item);

      if (enableLogging && !matches) {
        logger.debug(`🔍 ${logContext} - Item exclu (mode invité)`, logContext as any, {
          itemId: item.id,
          creatorId: item[creatorField],
          deviceId: userData.deviceId,
        });
      }

      return matches;
    }
  });

  if (enableLogging) {
    logger.info(`🔍 ${logContext} - Filtrage terminé`, logContext as any, {
      totalItems: items.length,
      filteredItems: filtered.length,
      mode: userData.isAuthenticated ? "connecté" : "invité",
      userId: userData.userId || "guest",
    });
  }

  return filtered;
}

/**
 * Helper pour charger des données selon le mode invité/connecté
 *
 * @param userData - Données utilisateur
 * @param loaders - Fonctions de chargement pour chaque mode
 * @returns Données chargées
 */
export async function loadDataByUserMode<T>(
  userData: UserData,
  loaders: {
    /** Fonction pour charger les données utilisateur connecté */
    authenticated: (userId: string) => Promise<T>;
    /** Fonction pour charger les données invité */
    guest: (deviceId: string) => Promise<T>;
    /** Fonction optionnelle pour filtrer les données invité (ex: polls guest) */
    guestFilter?: (data: T) => T;
  },
  options: {
    enableLogging?: boolean;
    logContext?: string;
  } = {},
): Promise<T> {
  const { enableLogging = false, logContext = "loadDataByUserMode" } = options;

  try {
    if (userData.isAuthenticated) {
      if (enableLogging) {
        logger.debug(`🔍 ${logContext} - Chargement mode connecté`, logContext as any, {
          userId: userData.userId,
        });
      }

      return await loaders.authenticated(userData.userId!);
    } else {
      if (enableLogging) {
        logger.debug(`🔍 ${logContext} - Chargement mode invité`, logContext as any, {
          deviceId: userData.deviceId,
        });
      }

      const data = await loaders.guest(userData.deviceId);

      // Appliquer le filtre invité si fourni
      if (loaders.guestFilter) {
        const filteredData = loaders.guestFilter(data);

        if (enableLogging) {
          logger.debug(`🔍 ${logContext} - Filtrage invité appliqué`, logContext as any, {
            before: Array.isArray(data) ? data.length : "N/A",
            after: Array.isArray(filteredData) ? filteredData.length : "N/A",
          });
        }

        return filteredData;
      }

      return data;
    }
  } catch (error) {
    logger.error(`❌ ${logContext} - Erreur chargement données`, logContext as any, {
      mode: userData.isAuthenticated ? "connecté" : "invité",
      userId: userData.userId || "guest",
      error,
    });

    throw error;
  }
}

export default useUserData;
